import { CartHandlerService } from '@literature/services/cart-handler.service';
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  Inject,
} from '@angular/core';
import { ICellRendererParams } from '@ag-grid-community/core';
import { AnalyticsService, ResponsiveService } from '@frk/eds-components';
import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { IUserProfile, UserRole } from '@services/profile.interface';
import { Logger } from '@utils/logger';
import { Observable, Subject, Subscription } from 'rxjs';
import get from 'lodash/get';
import { filter, takeUntil } from 'rxjs/operators';
import {
  CartActionPerformed,
  LiteratureDocument,
  LiteratureDocumentListingData,
} from '@types';
import { LiteratureRulesHelperService } from '@literature/services/literature-rules-helper.service';
import { LiteratureDataService } from '@literature/services/literature-data.service';
import { extractFileInfoFromLink } from '@utils/link-utils';
import { SH } from '@utils/app.constants';
import { SignInService } from '@services/sign-in.service';
import { LiteratureDocumentListingState } from '@types';
import { WINDOW } from '@ng-web-apis/common';

/**
 * Logger
 */
const logger = Logger.getLogger('DocumentDownloadComponent');

/**
 * FT Document download
 */
@Component({
  selector: 'ft-document-download',
  templateUrl: './document-download.component.html',
  styleUrls: ['./document-download.component.scss'],
})
export class DocumentDownloadComponent
  implements ICellRendererAngularComp, OnDestroy, OnChanges, OnInit {
  @Input() cellRendererData: LiteratureDocumentListingData;
  /**
   * Event Id used in edsButton directive
   */
  eventId = 'file_download';

  /**
   * Custom parameters to be used in edsButton directive
   */
  eventParams = {};

  /**
   * Params
   */
  public params;
  public documentDownloadLink: string;
  public addedInCart = false;
  public addedInProgress = false;
  public loginRequired: boolean;
  public profileData: IUserProfile;
  public cartSubscription: Subscription;
  public loginSubscription: Subscription;
  public showLogIn: boolean;
  public isDeleteEnable: boolean;
  public isSiteIntl: boolean;
  public isColumnLayout: boolean;
  public isCartConfigured: boolean;
  public componentName: string;
  private unsubscribe$: Subject<void> = new Subject<void>();
  protected cartState$: Observable<LiteratureDocumentListingState>;
  public isMobile$: Observable<boolean>;
  public isPrintOnly = false;

  /**
   * Constructor
   */
  constructor(
    @Inject(WINDOW) readonly windowRef: Window,
    private cartService: CartHandlerService,
    private cartHandlerService: CartHandlerService,
    private literatureRules: LiteratureRulesHelperService,
    private analytics: AnalyticsService,
    private literatureDataService: LiteratureDataService,
    private signInService: SignInService,
    private responsiveService: ResponsiveService
  ) {
    this.isMobile$ = this.responsiveService.isMobile$();
  }

  ngOnInit(): void {
    this.cartHandlerService
      .getAddedObservable$()
      ?.pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        // we reset 'in progress' always when cart state changes
        this.addedInProgress = false;
      });
  }

  ngOnChanges(): void {
    if (this.cellRendererData) {
      this.agInit(this.cellRendererData);
    }
  }

  refresh(params: ICellRendererParams): boolean {
    this.params = params;
    return true;
  }

  /**
   * On ag init
   */
  agInit(params) {
    this.params = params;
    this.documentDownloadLink = this.getDownloadLinkWithPreview(params?.data);

    this.isSiteIntl = get(params, 'isSiteIntl', true);
    this.isColumnLayout = get(params, 'isColumnLayout', false);
    this.componentName = get(params, 'componentName', 'default');
    if (!this.isSiteIntl) {
      this.loginRequired = this.params?.data?.literatureData?.loginRequired;
      this.checkCartStatus(this.params?.data?.literatureData);
      this.literatureRules.checkLiteratureRules(
        this.params?.data?.literatureData
      );
      this.isDownloadAvailable();
    }

    this.cartSubscription = this.cartService
      .getDeletedDocId()
      ?.subscribe((docIds: string[]) => {
        if (docIds?.includes(this.params?.data?.literatureData?.docId)) {
          this.addedInCart = false;
        }
      });
    this.loginSubscription = this.literatureDataService
      .getProfileData$()
      ?.pipe(
        takeUntil(this.unsubscribe$),
        filter((profile) => !!profile)
      )
      .subscribe((data: IUserProfile) => {
        this.profileData = data;
        this.showLogIn =
          !this.profileData.isLoggedIn ||
          UserRole[this.profileData?.profileInfo?.role] ===
            SH.toLocaleLowerCase();
      });

    this.isDeleteEnable = this.params.action.getDeleteConfiguration();
    this.isCartConfigured = this.params.action.getCartConfiguration();
  }

  /**
   * @param data document data
   * @return string
   */
  // TODO write test
  getDownloadLinkWithPreview(data: LiteratureDocumentListingData): string {
    return data.literatureData?.usePreviewLink &&
      data?.literatureData?.shareclass?.previewLink &&
      data?.literatureData?.dctermsType
      ? data?.literatureData?.shareclass?.previewLink +
          '#' +
          data?.literatureData?.dctermsType?.toLowerCase()
      : data?.literatureData?.pdfDownloadLink;
  }

  /**
   * Parses params and converts them to event object
   * @params - LiteratureDocument
   */
  fireDownloadAnalytics(params: LiteratureDocument): void {
    const { fileName }: { fileName: string } = extractFileInfoFromLink(
      params.pdfDownloadLink
    );

    this.analytics.trackEvent({
      location: '',
      event: this.eventId,
      file_name: fileName,
      file_extension: params.fileExtension,
      link_url: params.pdfDownloadLink,
      embed_id: params?.externalId || '',
      document_title: params?.dctermsTitle || '',
      document_source: params?.documentSourceSystem || '',
    });
  }

  /**
   * check the cart status if docuemnt is already in cart
   * @param litData LiteratureDocument
   */
  checkCartStatus(litData: LiteratureDocument): void {
    this.addedInCart = this.cartService.checkCartStatus(litData.docId);
  }

  /**
   *
   * @param data LiteratureDocumentListingData
   * @param event MouseEvent
   * @param addedInCart boolean flag for item added to cart
   */
  updateCartHandler(
    data: LiteratureDocumentListingData,
    event: MouseEvent,
    addedInCart: boolean
  ) {
    event.stopPropagation();

    this.addedInProgress = true;

    this.analytics.trackEvent({
      event: 'cart_engagement',
      action: addedInCart ? 'removed from cart' : 'add to cart',
      file_name: data.literatureData?.dctermsTitle,
      file_lit_code: data.literatureData?.docId,
    });

    if (addedInCart) {
      this.cartService.removeFromCart([data?.literatureData?.docId]);
      this.addedInCart = false;
      return;
    }

    this.addedInCart = true;
    if (
      this.params.actionPerformForCart ===
      CartActionPerformed.ADDED_FROM_MY_LITERATURE
    ) {
      this.cartService.addToCart(
        [data],
        CartActionPerformed.ADDED_FROM_MY_LITERATURE
      );
    } else {
      this.cartService.addToCart([data], CartActionPerformed.ADDED_FROM_GRID);
    }
  }

  /**
   * place the order for selected cart items
   * @param data LiteratureDocument
   */
  checkoutOrder(data: LiteratureDocument): void {
    this.cartService.checkoutOrder([{ literatureCode: data.docId, qty: 1 }]);
    this.fireDownloadAnalytics(data);
  }

  goToLink(data: LiteratureDocument, link: string) {
    this.checkoutOrder(data);
    this.windowRef.open(link, '_blank');
  }

  ngOnDestroy(): void {
    this.cartSubscription?.unsubscribe();
    this.loginSubscription?.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Deleted clicked row from grid
   * @param event mouse click event
   */
  deleteRow(event: MouseEvent): void {
    event.stopPropagation();
    this.params.action.deleteRow(this.params.node);
  }

  public signIn(): void {
    this.signInService.showSignInModal();
  }

  /**
   * Checks if download is available for a document based on the printOnly field and downloadUrl values
   */
  isDownloadAvailable(): void {
    // covers scenarios for printOnly as 'Y', 'N' and undefined
    this.isPrintOnly =
      this.params.data.literatureData.printOnly === 'Y' &&
      this.params?.data?.literatureData.downloadUrl
        ? true
        : false;
  }
}
